import { Action } from 'redux-actions';
import { all, put, select, takeEvery } from 'redux-saga/effects';

import { logCritical } from '@savgroup-front-common/configuration/appInsights/AppInsights';
import { executeSagaRequest } from '@savgroup-front-common/core/services';

import { getAssignablePermissionsRequest } from '../../api/Authorization/getAssignablePermissionsQuery';
import { listManagementUsersRequest } from '../../api/Authorization/listManagementUsersQuery';
import { getConfigurableRolesRequest } from '../../api/Authorization/roles/getConfigurableRolesQuery';
import { deleteUserProfileRequest } from '../../api/Authorization/user/deleteUserProfileCommand';
import { listUserProfileRequest } from '../../api/Authorization/user/listUserProfileQuery';
import { setUserProfilesToUserRequest } from '../../api/Authorization/user/setUserProfilesToUser';
import { upsertUserProfileRequest } from '../../api/Authorization/user/upsertUserProfileCommand';

import {
  GetUserProfilesPayload,
  SetUserProfilePayload,
} from './actionCreators';
import * as ActionTypes from './actionTypes';
import { DEFAULT_ENTITY_TYPE } from './constants';
import {
  getAssignablePermissionsIsLoadingSelector,
  getConfigurableRolesIsLoading,
  getUserManagementIsLoadingSelector,
  selectGetUserProfilByTenantIsLoading,
} from './selectors';

function* getManagementUsersWorker() {
  const isLoading: ReturnType<typeof getUserManagementIsLoadingSelector> =
    yield select((state) =>
      getUserManagementIsLoadingSelector(state, {
        entityType: DEFAULT_ENTITY_TYPE,
      }),
    );

  if (!isLoading) {
    yield executeSagaRequest(
      ActionTypes.GET_MANAGEMENT_USERS,
      listManagementUsersRequest({ entityType: DEFAULT_ENTITY_TYPE }),
      { indexer: DEFAULT_ENTITY_TYPE },
    );
    yield put(ActionTypes.GET_MANAGEMENT_USERS.end());
  }
}
function* getManagementUsersWatcher() {
  yield takeEvery<Action<void>>(
    ActionTypes.GET_MANAGEMENT_USERS.BASE,
    getManagementUsersWorker,
  );
}

function* getConfigurableRolesWorker() {
  const isLoading: ReturnType<typeof getConfigurableRolesIsLoading> =
    yield select((state) =>
      getConfigurableRolesIsLoading(state, { entityType: DEFAULT_ENTITY_TYPE }),
    );

  if (!isLoading) {
    yield executeSagaRequest(
      ActionTypes.GET_CONFIGURABLE_ROLES,
      getConfigurableRolesRequest({ entityType: DEFAULT_ENTITY_TYPE }),
      { indexer: DEFAULT_ENTITY_TYPE },
    );
    yield put(ActionTypes.GET_CONFIGURABLE_ROLES.end());
  }
}
function* getConfigurableRolesWatcher() {
  yield takeEvery(
    ActionTypes.GET_CONFIGURABLE_ROLES.BASE,
    getConfigurableRolesWorker,
  );
}

function* setUserProfilWorker({
  payload: {
    userProfileId,
    name,
    tenantId,
    roleIds,
    cumulDashboardIds,
    permissions,
  },
}: Action<SetUserProfilePayload>) {
  yield executeSagaRequest(
    ActionTypes.SET_USER_PROFIL,
    upsertUserProfileRequest({
      userProfileId,
      name,
      tenantId,
      roleIds,
      cumulDashboardIds,
      permissions,
    }),
    { indexer: userProfileId },
  );

  yield put(ActionTypes.SET_USER_PROFIL.end());
}
function* setUserProfilWatcher() {
  yield takeEvery<Action<SetUserProfilePayload>>(
    ActionTypes.SET_USER_PROFIL.BASE,
    setUserProfilWorker,
  );
}

function* getAllUserProfilesWorker({
  payload,
}: Action<GetUserProfilesPayload>) {
  const { tenantId } = payload;
  const isLoading: ReturnType<typeof selectGetUserProfilByTenantIsLoading> =
    yield select((state) =>
      selectGetUserProfilByTenantIsLoading(state, { tenantId }),
    );

  const meta = { indexer: tenantId };

  if (!isLoading) {
    yield executeSagaRequest(
      ActionTypes.GET_USER_PROFILES,
      listUserProfileRequest({ tenantId }),
      meta,
    );

    yield put(ActionTypes.GET_USER_PROFILES.end());
  }
}
function* getAllUserProfilesWatcher() {
  yield takeEvery<Action<GetUserProfilesPayload>>(
    ActionTypes.GET_USER_PROFILES.BASE,
    getAllUserProfilesWorker,
  );
}

function* deleteUserProfilesWorker({
  payload,
}: Action<{ userProfileId: string }>) {
  const { userProfileId } = payload;

  yield executeSagaRequest(
    ActionTypes.DELETE_USER_PROFIL,
    deleteUserProfileRequest({ userProfileId }),
    { userProfileId, indexer: userProfileId },
  );

  yield put(ActionTypes.DELETE_USER_PROFIL.end({ userProfileId }));
}
function* deleteUserProfilesWatcher() {
  yield takeEvery<Action<{ userProfileId: string }>>(
    ActionTypes.DELETE_USER_PROFIL.BASE,
    deleteUserProfilesWorker,
  );
}

interface UpsertUserProfileToUserPayload {
  sellerId: string;
  userProfileIds: string[];
  userId: string;
}

function* upsertUserProfileToUserWorker({
  payload,
}: Action<UpsertUserProfileToUserPayload>) {
  const { sellerId, userId, userProfileIds = [] } = payload;

  yield executeSagaRequest(
    ActionTypes.SET_USER_PROFILE_TO_USER,
    setUserProfilesToUserRequest({ sellerId, userProfileIds, userId }),
    { indexer: userId },
  );

  yield put(ActionTypes.SET_USER_PROFILE_TO_USER.end({ indexer: userId }));
}
function* upsertUserProfileToUserWatcher() {
  yield takeEvery<Action<UpsertUserProfileToUserPayload>>(
    ActionTypes.SET_USER_PROFILE_TO_USER.BASE,
    upsertUserProfileToUserWorker,
  );
}
function* getAssignablePermissionsWorker() {
  const isLoading: ReturnType<
    typeof getAssignablePermissionsIsLoadingSelector
  > = yield select(getAssignablePermissionsIsLoadingSelector);

  if (isLoading) {
    return;
  }

  yield executeSagaRequest(
    ActionTypes.GET_ASSIGNABLE_PERMISSIONS,
    getAssignablePermissionsRequest(),
  );

  yield put(ActionTypes.GET_ASSIGNABLE_PERMISSIONS.end());
}
function* getAssignablePermissionsWatcher() {
  yield takeEvery(
    ActionTypes.GET_ASSIGNABLE_PERMISSIONS.BASE,
    getAssignablePermissionsWorker,
  );
}

export default function* userImportSaga() {
  try {
    yield all([
      // setUserAndProfileWatcher(),
      // createUserManagementWatcher(),
      getManagementUsersWatcher(),
      getConfigurableRolesWatcher(),
      setUserProfilWatcher(),
      getAllUserProfilesWatcher(),
      deleteUserProfilesWatcher(),
      upsertUserProfileToUserWatcher(),
      getAssignablePermissionsWatcher(),
    ]);
  } catch (error) {
    logCritical(error as any);
  }
}
